import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import React, { useMemo, useState, useEffect } from 'react';

import { Stack, Button, TextField } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import FormCard from './FormCard';
import FormSection from './FormSection';
import { useAuth } from '../../../hooks/useAuth';
import Iconify from '../../../components/iconify';
import { useToast } from '../../../hooks/useToast';
import { useFormContext } from '../../../hooks/useForm';
import SelectInput from '../../../components/FormInputs/SelectInput';
import { fetchDepartments } from '../../../services/department.service';
import { createForm, modifyForm } from '../../../services/form.service';

FormBody.propTypes = {
  updateMode: PropTypes.bool,
  formId: PropTypes.number,
  handleClose: PropTypes.func,
};

export default function FormBody({ updateMode, formId, handleClose }) {
  const {
    sections,
    addSection,
    updateFormData,
    getFormData,
    formData,
    removeQuestionAnswers,
  } = useFormContext();

  const { id, departmentIds } = useAuth();
  const { isSmallScreen } = useResponsiveScreen();
  const { showToast, hideToast } = useToast();
  const [focusedSection, setFocusedSection] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(
    formData.department || null
  );
  const [sectionsArray, setSectionsArray] = useState([]);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorsTags, setErrorsTags] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: {
      formName: formData.title || '',
      formDescription: formData.description || '',
    },
  });

  useEffect(() => {
    setSectionsArray(Object.values(sections));
  }, [sections]);

  useEffect(() => {
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setDepartments
    );
  }, [t, showToast, hideToast]);

  const departmentList = useMemo(() => {
    if (!departments) return [];
    return departments
      .filter((dep) => (departmentIds || []).includes(dep.id))
      .map((dep) => ({ text: dep.name, value: dep.id }));
  }, [departments, departmentIds]);

  const onSubmit = async (data) => {
    if (isDebouncing) return;

    const currentFormData = getFormData();
    const allQuestions = Object.values(currentFormData.formData.questions || {});
    const hasIncompleteAttribute = allQuestions.some(
      (q) => q.attributeName && !q.attributeId
    );

    if (hasIncompleteAttribute) {
      showToast({
        message:
          t('formControl.completeAttributes') ||
          'Please select a valid attribute for all attribute fields',
        severity: 'error',
        props: { hideToast },
      });
      return;
    }

    setIsDebouncing(true);
    removeQuestionAnswers();

    // 2. Sync Metadata (Ensure final sync on submit)
    await updateFormData({
      title: data.formName,
      department: selectedDepartment,
      description: data.formDescription,
    });

    const finalPayload = getFormData();
    const requestData = {
      title: data.formName,
      departmentId: selectedDepartment,
      description: data.formDescription,
      formData: {
        ...finalPayload,
        formData: {
          ...finalPayload.formData,
          title: data.formName,
          department: selectedDepartment,
          description: data.formDescription,
        },
      },
      createdBy: id,
    };

    if (updateMode) {
      genericApiToaster(
        modifyForm,
        { ...requestData, id: formId },
        t('successMessages.successFormUpdate'),
        t('errorMessages.errorFormUpdate'),
        showToast,
        hideToast,
        t,
        handleClose
      );
    } else {
      genericApiToaster(
        createForm,
        requestData,
        t('successMessages.successFormCreate'),
        t('errorMessages.errorFormCreate'),
        showToast,
        hideToast,
        t,
        () => navigate('/dashboard/forms')
      );
    }

    setTimeout(() => setIsDebouncing(false), 500);
  };

  const hasErrors = useMemo(() => Object.keys(errorsTags).length > 0, [errorsTags]);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="row" justifyContent="center" mb={isSmallScreen ? 2 : 5}>
          <Button
            variant="contained"
            startIcon={
              <Iconify icon={updateMode ? 'radix-icons:update' : 'eva:plus-fill'} />
            }
            type="submit"
            disabled={
              !isValid ||
              !selectedDepartment ||
              sectionsArray.length === 0 ||
              isDebouncing ||
              hasErrors
            }
          >
            {updateMode ? t('buttons.updateForm') : t('buttons.createForm')}
          </Button>
        </Stack>

        <FormCard isFormHeader>
          <TextField
            fullWidth
            label={t('pages.formPage.formTitle')}
            variant="standard"
            error={!!errors.formName}
            helperText={errors.formName?.message}
            {...register('formName', {
              required: t('formControl.enterName'),
              onBlur: (e) => updateFormData({ title: e.target.value }),
            })}
          />

          <TextField
            fullWidth
            multiline
            label={t('pages.formPage.formDescription')}
            variant="standard"
            sx={{ mt: 2, mb: 2 }}
            {...register('formDescription', {
              onBlur: (e) => updateFormData({ description: e.target.value }),
            })}
          />

          {departmentList.length > 0 && (
            <SelectInput
              fullWidth
              labelValue={t('attributes.department')}
              defaultValue={selectedDepartment}
              options={departmentList}
              setSelectedInput={(val) => {
                setSelectedDepartment(val);
                updateFormData({ department: val });
              }}
              sx={{ mt: 2 }}
            />
          )}
        </FormCard>

        <Button
          sx={{ mt: 2, mb: 2 }}
          onClick={() =>
            !isDebouncing && addSection({ name: sectionsArray.length + 1, questions: [] })
          }
        >
          {t('pages.formPage.addSection')}
        </Button>

        {sectionsArray.map((section, index) => (
          <FormSection
            key={section.id}
            section={section}
            index={index + 1}
            sectionLength={sectionsArray.length}
            setFocusedSection={setFocusedSection}
            focusedSection={focusedSection}
            selectedDepartment={selectedDepartment}
            errorsTags={errorsTags}
            setErrorsTags={setErrorsTags}
          />
        ))}

        <Button
          sx={{ mt: 2, mb: 2 }}
          onClick={() =>
            !isDebouncing && addSection({ name: sectionsArray.length + 1, questions: [] })
          }
        >
          {t('pages.formPage.addSection')}
        </Button>

        <Stack direction="row" justifyContent="center" mt={5}>
          <Button
            variant="contained"
            type="submit"
            disabled={
              !isValid ||
              !selectedDepartment ||
              sectionsArray.length === 0 ||
              isDebouncing ||
              hasErrors
            }
          >
            {updateMode ? t('buttons.updateForm') : t('buttons.createForm')}
          </Button>
        </Stack>
      </form>
    </div>
  );
}
